// src/models/userModel.js
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, arrayRemove } from 'firebase/firestore';

async function getUserData(uid) {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    // Log minimally to avoid noisy tests while still aiding diagnostics
    console.warn('getUserData Firestore error', {
      path: `users/${uid}`,
      code: error?.code,
      message: error?.message,
    });
    return null;
  }
}

async function createUser(uid, data) {
  const userRef = doc(db, 'users', uid);
  const payload = {
    ...data,
    createdAt: new Date(),
    friends: [],
    deviceTokens: [],
  };
  await setDoc(userRef, payload);
}

async function updateUserData(uid, updates) {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, updates);
}

// Backwards-compatible export expected by Homepage.js
async function updateUserDeviceToken(uid, token) {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    const currentTokens = userDoc.exists() ? (userDoc.data().deviceTokens || []) : [];
    if (!currentTokens.includes(token)) {
      const updated = [...currentTokens, token];
      await updateDoc(userRef, { deviceTokens: updated }).catch(() => {});
    }

    // Write to the canonical devices subcollection used by Cloud Functions
    const deviceRef = doc(db, `users/${uid}/devices/${token}`);
    // Include createdAt only on first write to satisfy security rules
    const existingDevice = await getDoc(deviceRef).catch(() => null);
    const payload = {
      token,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      updatedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    };
    if (!existingDevice || !existingDevice.exists()) {
      payload.createdAt = serverTimestamp();
    }
    await setDoc(deviceRef, payload, { merge: true });
  } catch (error) {
    console.warn('updateUserDeviceToken Firestore error', {
      path: `users/${uid}`,
      code: error?.code,
      message: error?.message,
    });
  }
}

// Remove a device token from both legacy array and canonical subcollection
async function removeUserDeviceToken(uid, token) {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { deviceTokens: arrayRemove(token) }).catch(() => {});
    await deleteDoc(doc(db, `users/${uid}/devices/${token}`)).catch(() => {});
  } catch (_) {}
}

export { getUserData, createUser, updateUserData, updateUserDeviceToken, removeUserDeviceToken };
